import React from "react";
import { usePublicData } from "@/pages/public/PublicLayout";
import { useBodyLock } from "@/pages/public/useBodyLock";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

const ctx = { setOpen: null };

export function useInquire() {
  return {
    open: (payload) => {
      if (ctx.setOpen) ctx.setOpen(payload || {});
      else window.dispatchEvent(new CustomEvent("2high:inquire", { detail: payload || {} }));
    },
  };
}

export function InquireModal() {
  const [state, setState] = React.useState(null);
  const [saving, setSaving] = React.useState(false);
  const [sent, setSent] = React.useState(false);
  const { content } = usePublicData() || { content: {} };
  const c = content || {};

  const tgUrl = c.contact_telegram_url || "";
  const tgHandle = c.contact_telegram_handle || "";
  const waUrl = c.contact_whatsapp_url || "";
  const smsUrl = c.contact_sms_url || "";
  const phone = c.contact_phone || c.contact_phone_handle || "";
  const igUrl = c.contact_instagram_url || "";

  React.useEffect(() => {
    ctx.setOpen = (p) => { setState(p); setSent(false); };
    const onEvt = (e) => { setState(e.detail || {}); setSent(false); };
    window.addEventListener("2high:inquire", onEvt);
    return () => {
      ctx.setOpen = null;
      window.removeEventListener("2high:inquire", onEvt);
    };
  }, []);

  const open = !!state;
  const close = () => { setState(null); setSent(false); };

  // Ref-counted body lock — only engaged while modal is open.
  useBodyLock(open);

  async function submit(e) {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    const payload = {
      name: (f.get("name") || "").toString(),
      region: (f.get("region") || "").toString(),
      telegram: (f.get("telegram") || "").toString(),
      path: (f.get("path") || "").toString(),
      note: [
        (state && state.productName) ? `Interested in: ${state.productName}` : "",
        (f.get("note") || "").toString(),
      ].filter(Boolean).join("\n"),
    };
    setSaving(true);
    try {
      const r = await fetch(`${BACKEND}/api/public/inquiry`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (r.status === 429) throw new Error("Too many requests — please try again in a few minutes.");
      if (!r.ok) throw new Error("Could not send. Try again shortly.");
      setSent(true);
    } catch (ex) {
      alert(ex.message || "Could not send.");
    } finally { setSaving(false); }
  }

  return (
    <div
      className={`inq-panel${open ? " show" : ""}`}
      id="inqPanel"
      role="dialog"
      aria-modal="true"
      aria-labelledby="inqTitle"
      data-testid="inq-panel"
      onClick={(e) => { if (e.target === e.currentTarget) close(); }}
    >
      <div className="inq-card">
        <button className="x" onClick={close} data-testid="inq-close-btn" aria-label="Close">✕</button>
        <div className="inq-body">
        {sent ? (
          <>
            <div className="ml">— SENT · WE'LL BE IN TOUCH —</div>
            <h3 id="inqTitle">Inquiry <em>received.</em></h3>
            <p className="sub">We'll reach out on Telegram shortly. Keep your @ handle close.</p>
            <div className="actions">
              <button type="button" className="btn solid" onClick={close} data-testid="inq-done-btn">Close</button>
            </div>
          </>
        ) : (
          <>
            <div className="ml">— PRIVATE LINE · MEMBER INQUIRY —</div>
            <h3 id="inqTitle">
              Tell us what you're <em>after.</em>
            </h3>
            <p className="sub">Reach us instantly on Telegram or WhatsApp, or send the form and we'll confirm.</p>

            {/* Quick contact buttons — direct DM without filling the form */}
            <div className="inq-quick" style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 16 }}>
              {tgUrl && (
                <a
                  href={tgUrl}
                  target="_blank" rel="noreferrer"
                  data-testid="inq-quick-tg"
                  style={qBtnStyle("#1f4d6e", "#7fbfff")}
                >
                  <span style={{ fontSize: 18 }}>✈</span>
                  <span style={{ fontSize: 9.5, letterSpacing: ".18em", textTransform: "uppercase", marginTop: 4, opacity: .8 }}>Telegram</span>
                  <span style={{ fontSize: 11, marginTop: 2, fontWeight: 600 }}>{tgHandle || "Open chat"}</span>
                </a>
              )}
              {waUrl && (
                <a
                  href={waUrl}
                  target="_blank" rel="noreferrer"
                  data-testid="inq-quick-wa"
                  style={qBtnStyle("#1f4f2a", "#82e69b")}
                >
                  <span style={{ fontSize: 18 }}>✓</span>
                  <span style={{ fontSize: 9.5, letterSpacing: ".18em", textTransform: "uppercase", marginTop: 4, opacity: .8 }}>WhatsApp</span>
                  <span style={{ fontSize: 11, marginTop: 2, fontWeight: 600 }}>{phone || "Open chat"}</span>
                </a>
              )}
              {smsUrl && (
                <a
                  href={smsUrl}
                  data-testid="inq-quick-sms"
                  style={qBtnStyle("#3a3215", "#e6c87a")}
                >
                  <span style={{ fontSize: 18 }}>✎</span>
                  <span style={{ fontSize: 9.5, letterSpacing: ".18em", textTransform: "uppercase", marginTop: 4, opacity: .8 }}>Text · SMS</span>
                  <span style={{ fontSize: 11, marginTop: 2, fontWeight: 600 }}>{phone || "Send text"}</span>
                </a>
              )}
            </div>
            {igUrl && (
              <div style={{ textAlign: "center", marginBottom: 14, fontSize: 11, color: "#8e8f7f" }}>
                or follow on <a href={igUrl} target="_blank" rel="noreferrer" style={{ color: "#c9a86b", textDecoration: "underline" }}>{c.contact_instagram_handle || "Instagram"}</a>
              </div>
            )}

            <div style={{ position: "relative", textAlign: "center", margin: "6px 0 14px" }}>
              <span style={{ position: "absolute", inset: "50% 0 0", borderTop: "1px solid rgba(255,255,255,.08)" }}/>
              <span style={{ position: "relative", padding: "0 12px", background: "var(--bg, #0a0c08)", fontSize: 9.5, letterSpacing: ".22em", textTransform: "uppercase", color: "#6e6f5f" }}>
                or send an inquiry
              </span>
            </div>

            <form onSubmit={submit} autoComplete="off">
              <div className="inq-fields">
                <div className="fl">
                  <input type="text" name="name" placeholder=" " required data-testid="inq-name" />
                  <label>Your Name</label>
                </div>
                <div className="fl">
                  <input type="text" name="region" placeholder=" " required data-testid="inq-region" />
                  <label>Region / City</label>
                </div>
                <div className="fl">
                  <input type="text" name="telegram" placeholder=" " required data-testid="inq-tg" />
                  <label>Telegram @</label>
                </div>
                <div className="fl">
                  <select name="path" required defaultValue="" data-testid="inq-path">
                    <option value="" disabled></option>
                    <option value="Retail Menu">Retail Menu</option>
                    <option value="Custom Flight">Custom Flight</option>
                    <option value="Wholesale (QP+)">Wholesale (QP+)</option>
                    <option value="Just Browsing">Just Browsing</option>
                  </select>
                  <label>Buyer Path</label>
                </div>
                <div className="fl full">
                  <textarea name="note" placeholder=" " rows={2} data-testid="inq-note"
                    defaultValue={state && state.productName ? `Interested in: ${state.productName}` : ""} />
                  <label>Anything specific?</label>
                </div>
              </div>
              <div className="actions">
                <button type="submit" className="btn solid" disabled={saving} data-testid="inq-submit">
                  {saving ? "Sending…" : "Send · Open Telegram"}
                </button>
                <button type="button" className="btn" onClick={close}>Cancel</button>
              </div>
              <div className="legal">21+ only · Private collection · Members of record</div>
            </form>
          </>
        )}
        </div>
      </div>
    </div>
  );
}

/* Quick-contact button styling helper (Telegram/WhatsApp/SMS tiles) */
function qBtnStyle(bg, accent) {
  return {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "14px 8px",
    borderRadius: 10,
    background: `linear-gradient(180deg, ${bg}40, ${bg}20)`,
    border: `1px solid ${bg}`,
    color: accent,
    textDecoration: "none",
    transition: "transform .15s ease, border-color .15s ease",
    cursor: "pointer",
  };
}
